// src/layouts/SidebarLayout/index.tsx
import React, { ReactNode } from 'react';
import { Box, useTheme, useMediaQuery, alpha } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { SidebarProvider, useSidebar } from '../../contexts/SidebarContext';
import { SidebarLink, LayoutConfig } from './types';
import Header from './Header';
import Sidebar from './Sidebar';
import TabBar from '../../components/TabBar';

export interface SidebarLayoutProps {
  links: SidebarLink[];
  user: {
    name: string;
    email: string;
    role: string;
    avatar?: string;
  };
  config?: Partial<LayoutConfig>;
  children?: ReactNode;
}

const defaultConfig: LayoutConfig = {
  sidebarWidth: { collapsed: 72, expanded: 280 },
  headerHeight: 64,
  showTabs: true,
  showUserInfo: true,
  allowSidebarToggle: true,
};

const SidebarLayoutContent: React.FC<SidebarLayoutProps & { finalConfig: LayoutConfig }> = ({
  links,
  user,
  finalConfig,
  children
}) => {
  const theme = useTheme();
  const { getWidth, mobileOpen, isMobile, closeMobile } = useSidebar();
  const sidebarWidth = getWidth();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', position: 'relative' }}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          zIndex: theme.zIndex.drawer,
          transition: theme.transitions.create(['width', 'transform'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
          transform: isMobile && !mobileOpen ? 'translateX(-100%)' : 'translateX(0)',
          width: isMobile ? finalConfig.sidebarWidth.expanded : sidebarWidth,
        }}
      >
        <Sidebar links={links} config={finalConfig} />
      </Box>

      {isMobile && mobileOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: theme.zIndex.drawer - 1,
            cursor: 'pointer',
          }}
          onClick={closeMobile}
        />
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`
            : `linear-gradient(135deg, #f8fafc 0%, ${alpha(theme.palette.primary.main, 0.02)} 50%, #ffffff 100%)`,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'fixed',
            top: 0,
            left: sidebarWidth,
            right: 0,
            height: '100vh',
            background: theme.palette.mode === 'dark'
              ? 'radial-gradient(circle at 20% 20%, rgba(37, 99, 235, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.05) 0%, transparent 50%)'
              : 'radial-gradient(circle at 20% 20%, rgba(37, 99, 235, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.03) 0%, transparent 50%)',
            pointerEvents: 'none',
            zIndex: 0,
          },
          // Dynamic margin based on sidebar state
          marginLeft: `${sidebarWidth}px`,
          transition: theme.transitions.create('margin-left', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
          width: `calc(100% - ${sidebarWidth}px)`,
          maxWidth: `calc(100% - ${sidebarWidth}px)`,
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: theme.zIndex.appBar,
            backgroundColor: 'background.paper',
          }}
        >
          <Box
            sx={{
              height: finalConfig.headerHeight,
              minHeight: finalConfig.headerHeight,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Header user={user} config={finalConfig} />
          </Box>
          {finalConfig.showTabs && <TabBar />}
        </Box>

        <Box
          sx={{
            flex: 1,
            padding: theme.spacing(3),
            overflow: 'auto',
            minHeight: 0,
            maxWidth: '100%',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {children ?? <Outlet />}
        </Box>
      </Box>
    </Box>
  );
};

const SidebarLayout: React.FC<SidebarLayoutProps> = ({
  links,
  user,
  config = {},
  children
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const finalConfig: LayoutConfig = { ...defaultConfig, ...config };

  return (
    <SidebarProvider
      initialCollapsed={false}
      widthConfig={finalConfig.sidebarWidth}
    >
      <SidebarLayoutContent
        links={links}
        user={user}
        finalConfig={finalConfig}
        children={children}
      />
    </SidebarProvider>
  );
};

export default SidebarLayout;